import { KeyboardEvent, RefObject, useEffect, useRef, useState } from "react"
import ContentHeader from "../../components/ContentHeader"
import useAppContext from "../../hooks/useAppContext"
import useAuthContext from "../../hooks/useAuthContext"
import Configs from "../../Configs"
import AgencyPartnerService, { IAgencyPartner } from "../../services/AgencyPartnerService"
import ContentBody from "../../components/ContentBody"
import { Alert, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Table } from "react-bootstrap"
import Permissions from "../../constants/Permissions"
import Utils from "../../utils/Utils"
import Pagination from "../../components/Pagination"
import { SelectOptionType } from "../../constants/SelectOptionType"
import { SelectInstance } from "react-select"
import AsyncSelect from "react-select/async"
import AgencyService, { IAgency } from "../../services/AgencyService"
import { PartnerStatus } from "../../constants/PartnerStatus"
import Confirmation from "../../components/Confirmation"

function AgencyPartners() {
    const { hasPermission } = useAuthContext()
    const { state } = useAppContext()

    const [tableData, setTableData] = useState({
        data: new Array<IAgencyPartner>(),
        hasNext: false,
        hasPrevious: false,
        pageIndex: 1,
        pageSize: Configs.DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0
    })

    const [agencyPartner, setAgencyPartner] = useState<IAgencyPartner | null>(null)

    const [message, setMessage] = useState("")
    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [selectedItem, setSelectedItem] = useState({
        id: 0,
        name: "",
        action: ""
    })

    const keywordRef = useRef<HTMLInputElement>(null)

    const FilterFn = async (pageIndex: number) => {
        if (state.currentProviderId) {
            let pageSize = Configs.DEFAULT_PAGE_SIZE
            let keyword = keywordRef.current?.value || ""

            const res = await AgencyPartnerService.filter(state.currentProviderId, pageIndex, pageSize, keyword)

            if (res?.isSuccess) {
                setTableData(res.data)
            } else {
                console.log(res?.message)
            }

            setCurrentPageIndex(pageIndex)
        }
    }

    useEffect(() => {
        keywordRef.current?.focus()

        FilterFn(1)
    }, [state.currentProviderId])

    const handleSearch = () => {
        FilterFn(1)
    }

    const handleSearchInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            FilterFn(1)
        }
    }

    const handlePageChange = (pageNumber: number) => {
        FilterFn(pageNumber)
    }

    //Find a Agency
    const [messageFormModal, setMessageFormModal] = useState("")
    const [showModal, setShowModal] = useState(false)
    const handleCloseModal = () => {
        setShowModal(false)
        setAgencyPartner(null)
        setMessageFormModal("")
    }
    const handleShowModal = () => {
        setShowModal(true)
        setMessage("")
    }

    const handleFindAgency = () => {
        handleShowModal()
    }

    //AsyncSelect
    const [selectedOption, setSelectedOption] = useState(null as SelectOptionType | null)
    const asyncRef = useRef<SelectInstance<SelectOptionType> | null>(null)

    const handleSelectChange = async (option: SelectOptionType | null) => {
        setSelectedOption(option)

        if (option?.value) {
            let agencyId = option?.value as number

            if (state.currentProviderId && agencyId) {
                var res = await AgencyPartnerService.get(state.currentProviderId, agencyId)

                if (res.isSuccess) {
                    setAgencyPartner(res.data)

                    setSelectedOption(null)
                    asyncRef.current?.focus()
                } else {
                    setAgencyPartner(null)
                }
            }
        }
    }

    const filterAgencies = async (inputValue: string) => {
        var options: SelectOptionType[] = []

        if (state.currentProviderId) {
            var res = await AgencyService.search(inputValue)

            if (res.isSuccess && res.data) {
                options = res.data.map((item: IAgency) => {
                    return { value: item.id, label: item.name }
                })
            }
        }

        return options
    }

    const loadPlaceOptions = (inputValue: string) => new Promise<SelectOptionType[]>((resolve) => {
        resolve(filterAgencies(inputValue))
    })

    //Add
    const handleAdd = async (agencyId: number | null) => {
        if (state.currentProviderId && agencyId) {
            var res = await AgencyPartnerService.add(state.currentProviderId, agencyId)

            if (res.isSuccess) {
                FilterFn(currentPageIndex)
                handleCloseModal()
            } else {
                setMessageFormModal(res.message)
            }
        }
    }

    //Cancel
    const handleCancel = async (agencyId: number | null) => {
        if (state.currentProviderId && agencyId) {
            var res = await AgencyPartnerService.cancel(state.currentProviderId, agencyId)

            if (res.isSuccess) {
                FilterFn(currentPageIndex)
            }

            setMessage(res.message)
        }
    }

    //Remove
    const handleRemove = async (agencyId: number | null) => {
        if (state.currentProviderId && agencyId) {
            var res = await AgencyPartnerService.remove(state.currentProviderId, agencyId)

            if (res.isSuccess) {
                FilterFn(currentPageIndex)
            }

            setMessage(res.message)
        }
    }

    //Accept
    const handleAccept = async (agencyId: number | null) => {
        if (state.currentProviderId && agencyId) {
            var res = await AgencyPartnerService.accept(state.currentProviderId, agencyId)

            if (res.isSuccess) {
                FilterFn(currentPageIndex)
            }

            setMessage(res.message)
        }
    }

    //Reject
    const handleReject = async (agencyId: number | null) => {
        if (state.currentProviderId && agencyId) {
            var res = await AgencyPartnerService.reject(state.currentProviderId, agencyId)

            if (res.isSuccess) {
                FilterFn(currentPageIndex)
            }

            setMessage(res.message)
        }
    }

    //Confirmation
    const [messageConfirmation, setMessageConfirmation] = useState("")
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false)

    const hideConfirmationModal = () => {
        setDisplayConfirmationModal(false)
        setSelectedItem({
            id: 0,
            name: "",
            action: ""
        })
        setMessageConfirmation("")
    }

    const showConfirmation = (id: number, name: string, action: string) => {
        setSelectedItem({
            id: id,
            name: name,
            action: action
        })
        setDisplayConfirmationModal(true)

        setMessageConfirmation(() => {
            switch (action) {
                case "Cancel":
                    return `Are you sure you want to CANCEL the partner request with ${name}?`
                case "Remove":
                    return `Are you sure you want to REMOVE the partner request with ${name}?`
                    case "Reject":
                        return `Are you sure you want to REJECT the partner request from ${name}?`
                default:
                    return ""
            }
        })
    }

    const handleConfirm = async () => {
        if (selectedItem.action === "Cancel") {
            handleCancel(selectedItem.id)
        } else if (selectedItem.action === "Remove") {
            handleRemove(selectedItem.id)
        } else if (selectedItem.action === "Reject") {
            handleReject(selectedItem.id)
        }

        hideConfirmationModal()
    }

    return (
        <>
            <ContentHeader title="Agencies" />

            <ContentBody>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Header>
                                    {hasPermission(Permissions.AgencyPartners.Add) &&
                                        <Button variant="info" size="sm" onClick={handleFindAgency}>
                                            <i className="fas fa-search"></i> Find a agency
                                        </Button>
                                    }

                                    <div className="card-tools">
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="search"
                                                placeholder="Search"
                                                ref={keywordRef as RefObject<HTMLInputElement>}
                                                onKeyUp={handleSearchInputKeyPress}
                                            />
                                            <Button variant="info" size="sm" onClick={handleSearch}>
                                                <i className="fas fa-search"></i>
                                            </Button>
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {message !== "" && <Alert variant="info" onClose={() => setMessage("")} dismissible>{message}</Alert>}
                                    <Table striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Requested Time</th>
                                                <th>Name</th>
                                                <th>Address</th>
                                                <th>Tel</th>
                                                <th>Email</th>
                                                <th style={{ textAlign: "center", width: "80px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                tableData.data.map(item =>
                                                    <tr key={item.agency.id}>
                                                        <td>{Utils.dateToString(item.requestedTime, Configs.DATE_TIME_FORMAT)}</td>
                                                        <td>{item.agency.name || ""}</td>
                                                        <td>{item.agency.address || ""}</td>
                                                        <td>{item.agency.tel || ""}</td>
                                                        <td>{item.agency.email || ""}</td>
                                                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                            {item.status === PartnerStatus.PROVIDER_REQUESTED
                                                                && hasPermission(Permissions.AgencyPartners.Cancel)
                                                                && <Button variant="secondary" className="mr-1" size="sm" onClick={() => showConfirmation(item.agencyId, item.agency.name, "Cancel")}><i className="fas fa-times"></i> Cancel</Button>}
                                                            {item.status === PartnerStatus.ACCEPTED
                                                                && hasPermission(Permissions.AgencyPartners.Remove)
                                                                && <Button variant="danger" className="mr-1" size="sm" onClick={() => showConfirmation(item.agencyId, item.agency.name, "Remove")}><i className="fas fa-times"></i> Remove</Button>}
                                                            {item.status === PartnerStatus.AGENCY_REQUESTED
                                                                && hasPermission(Permissions.AgencyPartners.Accept)
                                                                && <Button variant="success" className="mr-1" size="sm" onClick={() => handleAccept(item.agencyId)}><i className="fas fa-check"></i> Accept</Button>}
                                                            {item.status === PartnerStatus.AGENCY_REQUESTED
                                                                && hasPermission(Permissions.AgencyPartners.Reject)
                                                                && <Button variant="default" className="mr-1" size="sm" onClick={() => showConfirmation(item.agencyId, item.agency.name, "Reject")}><i className="fas fa-times"></i> Reject</Button>}
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer>
                                    <Pagination hasNext={tableData.hasNext} hasPrevious={tableData.hasPrevious} pageIndex={tableData.pageIndex} pageSize={tableData.pageSize} totalPages={tableData.totalPages} totalRecords={tableData.totalRecords} handlePageChange={handlePageChange} />
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </ContentBody>

            <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Find a agency</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AsyncSelect isClearable value={selectedOption} loadOptions={loadPlaceOptions} onChange={handleSelectChange} ref={asyncRef} placeholder="Search agency" noOptionsMessage={({ inputValue }) => !inputValue ? "Enter agency to search." : "Can't find your agency!"} />
                    {messageFormModal !== "" && <Alert variant="danger" onClose={() => setMessageFormModal("")} dismissible>{messageFormModal}</Alert>}
                    {agencyPartner && <fieldset className="mt-3 pt-3">
                        <dl className="row mb-0">
                            <dt className="col-sm-3">Name</dt><dd className="col-sm-9">{agencyPartner.agency.name}</dd>
                            <dt className="col-sm-3">Adrress</dt><dd className="col-sm-9">{agencyPartner.agency.address}</dd>
                            <dt className="col-sm-3">Tel</dt><dd className="col-sm-9">{agencyPartner.agency.tel}</dd>
                            <dt className="col-sm-3">Email</dt><dd className="col-sm-9">{agencyPartner.agency.email}</dd>
                        </dl>
                        {!agencyPartner.agencyId && <>
                            <Button variant="info" size="sm" className="mt-3" onClick={() => handleAdd(agencyPartner.agency.id)}>
                                <i className="far fa-handshake"></i> Add agency
                            </Button>
                        </>}
                    </fieldset>}
                </Modal.Body>
            </Modal>

            <Confirmation show={displayConfirmationModal} message={messageConfirmation} onHide={hideConfirmationModal} onConfirm={handleConfirm}></Confirmation>
        </>
    )
}

export default AgencyPartners